import { useMutation, useQuery } from "@apollo/client";
import { Alert, Box, CircularProgress, Snackbar, Tab, Tabs, Typography } from "@mui/material";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AppPageNoTopPadding } from "@/layouts";

import { GetSingleUserQuery, GetSingleUserQueryVariables } from "../../graphql/graphql";
import { Account, CallSchedule, Playlist } from "../../types/api";
import FeatureFlags from "../../utils/featureFlags";
import CallPlans from "./components/CallPlans";
import { CallSchedules } from "./components/CallSchedules";
import { PlaylistsComponent } from "./components/Playlists";
import { UsageHistory } from "./components/UsageHistory";
import { UserInfoComponent } from "./components/UserInfo";
import { GET_TREATMENT_HISTORY } from "./components/UserInfo/components/TreatmentHistoryTable/queries";
import { TreatmentHistoryResponse } from "./components/UserInfo/components/TreatmentHistoryTable/types";
import { GET_SINGLE_USER, SEND_ACCESS_NOTIFICATION } from "./queries";
import { Loader } from "./styles";
import { AccessNotificationInput, TabPanelProps } from "./types";

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    <Box sx={{ p: 3 }}>
      <div>{children}</div>
    </Box>
  </div>
);

const a11yProps = (tabName: string) => ({
  id: `simple-tab-${tabName}`,
  "aria-controls": `simple-tabpanel-${tabName}`,
});

const TABS = [
  { slug: "patient-info", tabName: "Patient Info" }, // 0
  { slug: "playlists", tabName: "Playlists" }, // 1
  { slug: "call-plans", tabName: "Call Plans" }, // 2
  { slug: "usage-history", tabName: "Usage History" }, // 3
  { slug: "call-schedules", tabName: "Call Schedules" }, // 4
] as const;

const tabSlugToValue: Record<string, number> = TABS.reduce((acc, tab, index) => {
  acc[tab.slug] = index;
  return acc;
}, {});

const valueToTabSlug: Record<number, string> = TABS.reduce((acc, tab, index) => {
  acc[index] = tab.slug;
  return acc;
}, {});

export const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const showCallPlanTabEnabled = useBooleanFlagValue(FeatureFlags.SHOW_CALL_PLAN_TAB, false);
  const userId = parseInt(id ?? "") || 0;
  const pathPrefix = `/patient-details/${userId}`;
  const [tabValue, setTabValue] = useState<number>(tabSlugToValue[tab?.toLowerCase() ?? ""] ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);

  const handleTabChange = (_event, newValue: number) => {
    setTabValue(newValue);
  };

  const [sendAccessNotification] = useMutation<AccessNotificationInput>(SEND_ACCESS_NOTIFICATION, {
    variables: {
      accountId: userId,
      accessedBy: localStorage.getItem("username"),
      source: "admin panel",
    },
  });
  useEffect(() => {
    sendAccessNotification();
  }, [sendAccessNotification]);

  const { data, error, loading, refetch } = useQuery<
    GetSingleUserQuery,
    GetSingleUserQueryVariables
  >(GET_SINGLE_USER, {
    variables: { userId },
    fetchPolicy: "network-only",
  });

  const account = data?.account;

  const {
    data: TreatmentData,
    loading: TreatmentLoading,
    refetch: AllProgramsRefetch,
  } = useQuery<TreatmentHistoryResponse>(GET_TREATMENT_HISTORY, {
    variables: { accountId: account?.id },
    fetchPolicy: "network-only",
    skip: !account?.id,
  });

  useEffect(() => {
    setIsLoading(loading || TreatmentLoading);
  }, [loading, TreatmentLoading]);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  // When user uses back/forward (or manually edits the URL), keep the tab selection in sync.
  useEffect(() => {
    const fromUrl = tabSlugToValue[tab?.toLowerCase() ?? ""];
    if (!showCallPlanTabEnabled && fromUrl === tabSlugToValue["call-plans"]) {
      navigate(`${pathPrefix}/${TABS[0].slug}`, { replace: true });
      return;
    }

    if (fromUrl == null) {
      navigate(`${pathPrefix}/${valueToTabSlug[tabValue] ?? TABS[0].slug}`, { replace: true });
    } else if (fromUrl !== tabValue) {
      setTabValue(fromUrl);
    }
  }, [tab, tabValue, pathPrefix, navigate, showCallPlanTabEnabled]);

  useEffect(() => {
    if (TreatmentData?.treatmentHistory && TreatmentData.treatmentHistory.length > 0) {
      refetch();
    }
  }, [TreatmentData?.treatmentHistory, refetch]);

  const inMaintenance = account?.latest_program_type?.toLowerCase() === "maintenance";

  return (
    <AppPageNoTopPadding>
      {isLoading || !account ? (
        <Loader>
          <CircularProgress />
        </Loader>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box
              sx={{
                margin: "1rem 1rem 0 1rem",
                width: "max-content",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h6">
                {account?.firstName} {account?.lastName}
              </Typography>
            </Box>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
              {TABS.map((t, index) => {
                if (!showCallPlanTabEnabled && t.slug === "call-plans") {
                  return null;
                }

                return (
                  <Tab
                    key={t.slug}
                    component={Link}
                    to={`${pathPrefix}/${t.slug}`}
                    value={index}
                    label={t.tabName}
                    {...a11yProps(t.slug)}
                  />
                );
              })}
            </Tabs>
          </Box>
          <TabPanel value={tabValue} index={0}>
            <UserInfoComponent
              account={account as Account}
              AllProgramsData={TreatmentData}
              ProgramsReftech={AllProgramsRefetch}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PlaylistsComponent
              account={account as Account}
              AllProgramsData={TreatmentData}
              ProgramsReftech={AllProgramsRefetch}
              inMaintenance={inMaintenance}
            />
          </TabPanel>
          {showCallPlanTabEnabled && (
            <TabPanel value={tabValue} index={2}>
              <CallPlans accountId={userId} callPlans={account?.callPlans ?? []} />
            </TabPanel>
          )}
          <TabPanel value={tabValue} index={3}>
            <UsageHistory account={account as Account} accountId={userId} />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <CallSchedules
              callSchedules={(account?.callSchedules ?? []) as unknown as CallSchedule[]}
              userId={userId}
              playlists={(account?.playlists ?? []) as unknown as Playlist[]}
              inMaintenance={inMaintenance}
            />
          </TabPanel>
        </Box>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch single patient data.
        </Alert>
      </Snackbar>
    </AppPageNoTopPadding>
  );
};
